/**
 * Select — single-choice picker styled like a `<select>`, built on
 * `<Dropdown role="listbox">`.
 *
 * This is the ONE place that owns the "custom control that behaves like a
 * native select" pattern. Every call site that needs a styled select (as
 * opposed to Tailwind's unstyleable native `<select>` chrome) should use
 * this component instead of hand-rolling a `<Dropdown>` + trigger button —
 * that duplication is exactly how the ARIA role (menu vs. listbox) and the
 * keyboard/position wiring drift out of sync across call sites.
 *
 * Prefer a real `<select>` when you don't need custom styling — it's free
 * keyboard support, free mobile OS picker, and free screen-reader support.
 * Reach for this only when the design calls for a styled trigger.
 */

import { Dropdown } from "@components/ui/Dropdown"
import { cn } from "@lib/cn"
import { ChevronDown } from "lucide-react"
import { useId } from "react"

export interface SelectOption<T extends string> {
  value: T
  label: string
}

const defaultTriggerClass =
  "inline-flex h-8 w-full items-center gap-x-2 rounded-md bg-surface px-3 py-2 text-sm font-semibold text-ink shadow-xs ring-1 ring-border ring-inset hover:bg-inset focus:ring-2 focus:outline-none focus:ring-inset disabled:cursor-not-allowed disabled:opacity-60"

interface Props<T extends string> {
  value: T
  options: ReadonlyArray<SelectOption<T>>
  onChange: (value: T) => void
  id?: string
  name?: string
  ariaLabel?: string
  ariaLabelledBy?: string
  ariaDescribedBy?: string
  disabled?: boolean
  /** Shown in the trigger when `value` doesn't match any option (e.g. an unset "" state). */
  placeholder?: string
  /** Which edge of the trigger the panel aligns to. Default "left". */
  align?: "left" | "right"
  /** Classes on the `<Dropdown>` wrapper — controls width (`w-28`, `w-full`, …). */
  className?: string
  /** Full override of the trigger button's classes. Falls back to a sensible default. */
  triggerClassName?: string
  /** Extra classes on the listbox panel. */
  panelClassName?: string
}

export function Select<T extends string>({
  value,
  options,
  onChange,
  id,
  name,
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  disabled,
  placeholder,
  align = "left",
  className,
  triggerClassName,
  panelClassName,
}: Props<T>) {
  const selected = options.find((option) => option.value === value)
  const triggerLabel = selected?.label ?? placeholder ?? value
  const baseId = useId()
  const nameId = `${baseId}-name`
  const valueId = `${baseId}-value`
  // The accessible name is the label AND the current value — "Ordenar,
  // Promedio del Parcial 3 (alto a bajo)" — as a native <select> announces.
  // A bare aria-label replaced the button's text, so a screen reader heard
  // "Ordenar" and never what it was set to. Label first, so the name still
  // starts with the visible label (WCAG 2.5.3, label in name).
  const labelledBy = ariaLabelledBy
    ? `${ariaLabelledBy} ${valueId}`
    : ariaLabel
      ? `${nameId} ${valueId}`
      : undefined

  return (
    <Dropdown
      role="listbox"
      align={align}
      className={cn("w-full", className)}
      panelClassName={panelClassName}
      renderTrigger={({ ref, open, ...props }) => (
        <button
          ref={ref}
          id={id}
          name={name}
          type="button"
          disabled={disabled}
          {...props}
          aria-labelledby={labelledBy}
          aria-describedby={ariaDescribedBy}
          className={cn(triggerClassName ?? defaultTriggerClass)}
        >
          {ariaLabel ? (
            <span id={nameId} hidden>
              {ariaLabel}
            </span>
          ) : null}
          <span id={valueId} className="flex-1 truncate text-left">
            {triggerLabel}
          </span>
          <ChevronDown
            className={cn(
              "text-ink-subtle -mr-1 h-4 w-4 shrink-0 transition-transform duration-150",
              open && "rotate-180",
            )}
            aria-hidden
          />
        </button>
      )}
      groups={[
        {
          items: options.map((option) => ({
            label: option.label,
            checked: option.value === value,
            disabled,
            onClick: () => onChange(option.value),
          })),
        },
      ]}
    />
  )
}
